import React, { useState } from 'react';
import { UrlItem } from '../../models/source';
import { Dialog, Button, Spinner, Toggle } from '../ui';
import { Bug, Play, Trash2 } from '../ui/icons';

interface UrlActionsProps {
  urlItem: UrlItem;
  sourceId: string;
  onTest?: (urlItem: UrlItem) => void;
  onProcess?: (urlItem: UrlItem) => void;
  onToggleActive?: (urlItem: UrlItem) => void;
  onDelete?: (urlItem: UrlItem) => void;
  processing?: boolean;
  showDelete?: boolean;
  size?: 'small' | 'medium';
  orientation?: 'horizontal' | 'vertical';
}

export function UrlActions({
  urlItem,
  sourceId,
  onTest,
  onProcess,
  onToggleActive,
  onDelete,
  processing = false,
  showDelete = true,
  size = 'small',
  orientation = 'horizontal',
}: UrlActionsProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (onDelete) {
      setDeleting(true);
      try {
        await onDelete(urlItem);
        setDeleteDialogOpen(false);
      } catch (error) {
        console.error('Error deleting URL:', error);
      } finally {
        setDeleting(false);
      }
    }
  };

  const buttonSize = size === 'small' ? 'p-1.5' : 'p-2';

  return (
    <>
      <div
        className={`flex items-center ${
          orientation === 'vertical' ? 'flex-col gap-2' : 'flex-row gap-1'
        }`}
      >
        {onTest && (
          <button
            title="Debug/Test URL"
            className={`${buttonSize} rounded-lg text-primary-600 hover:bg-primary-50 transition-colors`}
            onClick={() => onTest(urlItem)}
          >
            <Bug className={size === 'small' ? 'h-4 w-4' : 'h-5 w-5'} />
          </button>
        )}

        {onProcess && (
          <button
            title="Process URL"
            className={`${buttonSize} rounded-lg text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={() => onProcess(urlItem)}
            disabled={processing}
          >
            {processing ? (
              <Spinner size={size === 'small' ? 'sm' : 'md'} />
            ) : (
              <Play className={size === 'small' ? 'h-4 w-4' : 'h-5 w-5'} />
            )}
          </button>
        )}

        {onToggleActive && (
          <div title={urlItem.active ? 'Disable URL' : 'Enable URL'}>
            <Toggle
              size={size === 'small' ? 'sm' : 'md'}
              checked={urlItem.active !== false}
              onChange={() => onToggleActive(urlItem)}
            />
          </div>
        )}

        {showDelete && onDelete && (
          urlItem.urlSource === 'MANUAL' ? (
            <button
              title="Delete URL"
              className={`${buttonSize} rounded-lg text-red-600 hover:bg-red-50 transition-colors`}
              onClick={handleDeleteClick}
            >
              <Trash2 className={size === 'small' ? 'h-4 w-4' : 'h-5 w-5'} />
            </button>
          ) : (
            <button
              title="Sitemap URLs cannot be deleted as they are managed automatically and may be re-added during the next sitemap processing job"
              className={`${buttonSize} rounded-lg text-red-600 opacity-50 cursor-not-allowed`}
              disabled
            >
              <Trash2 className={size === 'small' ? 'h-4 w-4' : 'h-5 w-5'} />
            </button>
          )
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete URL"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting && <Spinner size="sm" className="mr-2" />}
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-gray-900 mb-2">
          Are you sure you want to delete this URL?
        </p>
        <p className="text-sm text-gray-600">
          {urlItem.sourceUrl}
        </p>
      </Dialog>
    </>
  );
}
